import React, { useState, useEffect, useRef } from 'react';
import { Phone, X, ChevronUp, Volume2, VolumeX } from 'lucide-react';

const VoiceModeModal = ({ 
  isVisible, 
  onClose,
  currentVoice,
  voiceOptions,
  onVoiceSelected,
  initializeVoiceMode
}) => {
  const [stage, setStage] = useState('selection');
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);
  const [currentVoiceIndex, setCurrentVoiceIndex] = useState(0);
  const [selectedVoice, setSelectedVoice] = useState(currentVoice || null);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [isSwipeAnimating, setIsSwipeAnimating] = useState(false);
  const [swipeDirection, setSwipeDirection] = useState('');
  const [isPlayingPreview, setIsPlayingPreview] = useState(false);
  const audioRef = useRef(null);

  const minSwipeDistance = 50;

  const defaultVoiceOptions = [
    {
      id: 'guide',
      name: 'Emma',
      personality: 'Warm & friendly',
      description: 'Caring and conversational tone.',
      configId: 'Host 1',
      color: '#EC4899',
      audioFile: '/sound/voice1.wav',
    },
    {
      id: 'therapist',
      name: 'James',
      personality: 'Upbeat & energetic',
      description: 'Lively and engaging style.',
      configId: 'Host 2',
      color: '#4a90e2',
      audioFile: '/sound/bob-voice.wav',
    },
    {
      id: 'guest1',
      name: 'Jackie',
      personality: 'Relaxed & easygoing',
      description: 'Laid-back and approachable.',
      configId: 'Host 3',
      color: '#10b981',
      audioFile: '/sound/jackie-voice.wav',
    },
    {
      id: 'guest2',
      name: 'Kenny',
      personality: 'Smooth & conversational',
      description: 'Natural and flowing dialogue.',
      configId: 'Host 4',
      color: '#8b5cf6',
      audioFile: '/sound/alex-voice.wav',
    },
  ];

  const voices = voiceOptions?.length > 0 ? voiceOptions : defaultVoiceOptions;
  const currentVoiceData = voices[currentVoiceIndex];

  // SWIPE HANDLERS
  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => setTouchEnd(e.targetTouches[0].clientX);

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe || isRightSwipe) {
      setIsSwipeAnimating(true);
      setSwipeDirection(isLeftSwipe ? 'left' : 'right');
      setTimeout(() => {
        if (isLeftSwipe) nextVoice();
        if (isRightSwipe) prevVoice();
        setTimeout(() => {
          setIsSwipeAnimating(false);
          setSwipeDirection('');
        }, 300);
      }, 100);
    }
  };

  const nextVoice = () => {
    const newIndex = (currentVoiceIndex + 1) % voices.length;
    setCurrentVoiceIndex(newIndex);
  };

  const prevVoice = () => {
    const newIndex = (currentVoiceIndex - 1 + voices.length) % voices.length;
    setCurrentVoiceIndex(newIndex);
  };

  const handleVoiceSelect = () => {
    setSelectedVoice(currentVoiceData);
    onVoiceSelected?.(currentVoiceData);
    
    setIsAnimatingOut(true);
    setTimeout(() => {
      onClose();
      
      if (initializeVoiceMode) {
        initializeVoiceMode(currentVoiceData.configId);
      }
    }, 300);
  };

  const getBuddyClasses = () => {
    let classes = 'buddy-circle';
    if (isPlayingPreview) {
      classes += ' speaking';
    }
    if (isSwipeAnimating) {
      classes += ` swipe-${swipeDirection}`;
    }
    return classes;
  };

  const handleClose = () => {
    // Stop any playing audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setIsPlayingPreview(false);
    
    setIsAnimatingOut(true);
    setTimeout(() => {
      onClose();
    }, 350);
  };

  const handlePreview = async () => {
    const audioFile = currentVoiceData?.audioFile;
    
    if (!audioFile) {
      console.warn('No audio file for this voice. Current voice data:', currentVoiceData);
      return;
    }

    // Stop if already playing
    if (isPlayingPreview && audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlayingPreview(false);
      return;
    }

    try {
      // Create or reuse audio element
      if (!audioRef.current) {
        audioRef.current = new Audio();
      }

      audioRef.current.src = audioFile;
      audioRef.current.onended = () => setIsPlayingPreview(false);
      
      await audioRef.current.play();
      setIsPlayingPreview(true);
    } catch (error) {
      console.error('Error playing preview:', error);
      setIsPlayingPreview(false);
    }
  };

  // Stop audio when voice changes
  useEffect(() => {
    if (audioRef.current && isPlayingPreview) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlayingPreview(false);
    }
  }, [currentVoiceIndex]);

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  if (!isVisible) return null;

  if (stage === 'selection') {
    return (
      <div className={`voice-modal-overlay ${isAnimatingOut ? 'animating-out' : ''}`} onClick={handleClose}>
        <div className={`voice-modal-container ${isAnimatingOut ? 'slide-down' : ''}`} onClick={(e) => e.stopPropagation()}>
          
          {/* MOBILE & TABLET LAYOUT */}
          <div className="mobile-tablet-layout">
            <div className="voice-modal-header">
              <h3 className="voice-modal-title2">Pick a Voice</h3>
              <button className="voice-modal-close-button" onClick={handleClose}>
                <X size={20} />
              </button>
            </div>

            <div 
              className="voice-modal-content"
              onTouchStart={onTouchStart}
              onTouchMove={onTouchMove}
              onTouchEnd={onTouchEnd}
            >
              <div className="voice-ai-buddy">
                <div 
                  className={getBuddyClasses()} 
                  style={{ '--voice-color': currentVoiceData?.color || '#4a90e2' }}
                  onClick={handlePreview}
                >
                  <div className="buddy-inner">
                    <div className="wave-animation"></div>
                    <div className="buddy-core-alex"></div>
                  </div>
                </div>
                
                <div className={`dots-alex-container ${isPlayingPreview ? 'show' : ''}`}>
                  <div className="dot alex dot-1"></div>
                  <div className="dot alex dot-2"></div>
                  <div className="dot alex dot-3"></div>
                  <div className="dot alex dot-4"></div>
                </div>
              </div>

              <div className="voice-info">
                <h3 className="voice-name">{currentVoiceData?.name}</h3>
                <p className="voice-personality">{currentVoiceData?.personality}</p>
              </div>

              <div className="voice-controls">
                <button className="nav-button prev desktop-only" onClick={prevVoice}>‹</button>
                <button 
                  className={`preview-button ${isPlayingPreview ? 'playing' : ''}`}
                  onClick={handlePreview}
                >
                  {isPlayingPreview ? 'Stop' : 'Preview'}
                </button>
                <button className="nav-button next desktop-only" onClick={nextVoice}>›</button>
              </div>

              <div className="voice-indicators">
                {voices.map((_, index) => (
                  <div
                    key={index}
                    className={`indicator ${index === currentVoiceIndex ? 'active' : ''}`}
                    onClick={() => setCurrentVoiceIndex(index)}
                  />
                ))}
              </div>

              <button className="select-voice-button" onClick={handleVoiceSelect}>
                <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '8px' }}>
                  <path d="M4.5 2C3.11929 2 2 3.11929 2 4.5C2 5.88072 3.11929 7 4.5 7C5.88072 7 7 5.88072 7 4.5C7 3.11929 5.88072 2 4.5 2ZM3 4.5C3 3.67157 3.67157 3 4.5 3C5.32843 3 6 3.67157 6 4.5C6 5.32843 5.32843 6 4.5 6C3.67157 6 3 5.32843 3 4.5ZM10.5 2C9.11929 2 8 3.11929 8 4.5C8 5.88072 9.11929 7 10.5 7C11.8807 7 13 5.88072 13 4.5C13 3.11929 11.8807 2 10.5 2ZM9 4.5C9 3.67157 9.67157 3 10.5 3C11.3284 3 12 3.67157 12 4.5C12 5.32843 11.3284 6 10.5 6C9.67157 6 9 5.32843 9 4.5ZM2 10.5C2 9.11929 3.11929 8 4.5 8C5.88072 8 7 9.11929 7 10.5C7 11.8807 5.88072 13 4.5 13C3.11929 13 2 11.8807 2 10.5ZM4.5 9C3.67157 9 3 9.67157 3 10.5C3 11.3284 3.67157 12 4.5 12C5.32843 12 6 11.3284 6 10.5C6 9.67157 5.32843 9 4.5 9ZM10.5 8C9.11929 8 8 9.11929 8 10.5C8 11.8807 9.11929 13 10.5 13C11.8807 13 13 11.8807 13 10.5C13 9.11929 11.8807 8 10.5 8ZM9 10.5C9 9.67157 9.67157 9 10.5 9C11.3284 9 12 9.67157 12 10.5C12 11.3284 11.3284 12 10.5 12C9.67157 12 9 11.3284 9 10.5Z" fill="currentColor" fillRule="evenodd" clipRule="evenodd"></path>
                </svg>
                Select {currentVoiceData?.name}
              </button>

              <div className="swipe-instruction mobile-only">
                <em>swipe to explore other voices</em>
              </div>
            </div>
          </div>

          {/* DESKTOP LAYOUT - Grid of all voices */}
          <div className="desktop-layout">
            <div className="desktop-header">
              <h3 className="desktop-title">Pick a Voice</h3>
              <button className="voice-modal-close-button" onClick={handleClose}>
                <X size={22} />
              </button>
            </div>

            <div className="voices-grid">
              {voices.map((voice, index) => (
                <div
                  key={voice.id}
                  className={`voice-card ${currentVoiceIndex === index ? 'selected' : ''}`}
                  onClick={() => setCurrentVoiceIndex(index)}
                >
                  <div className="voice-card-orb">
                    <div 
                      className={`card-buddy-circle ${isPlayingPreview && currentVoiceIndex === index ? 'speaking' : ''}`}
                      style={{ '--voice-color': voice.color }}
                    >
                      <div className="card-buddy-inner">
                        <div className="card-buddy-core"></div>
                      </div>
                    </div>
                  </div>
                  <div className="voice-card-info">
                    <h4 className="voice-card-name">{voice.name}</h4>
                    <p className="voice-card-personality">{voice.personality}</p>
                  </div>
                  {currentVoiceIndex === index && (
                    <div className="selected-checkmark">
                      <svg width="16" height="16" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M11.4669 3.72684C11.7558 3.91574 11.8369 4.30308 11.648 4.59198L7.39799 11.092C7.29783 11.2452 7.13556 11.3467 6.95402 11.3699C6.77247 11.3931 6.58989 11.3355 6.45446 11.2124L3.70446 8.71241C3.44905 8.48022 3.43023 8.08494 3.66242 7.82953C3.89461 7.57412 4.28989 7.55529 4.5453 7.78749L6.75292 9.79441L10.6018 3.90792C10.7907 3.61902 11.178 3.53795 11.4669 3.72684Z" fill="currentColor" fillRule="evenodd" clipRule="evenodd"></path>
                      </svg>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="desktop-actions">
              <button 
                className={`desktop-preview-btn ${isPlayingPreview ? 'playing' : ''}`}
                onClick={handlePreview}
              >
                {isPlayingPreview ? (
                  <>
                    <VolumeX size={18} />
                    Stop Preview
                  </>
                ) : (
                  <>
                    <Volume2 size={18} />
                    Preview {currentVoiceData?.name}
                  </>
                )}
              </button>
              <button className="desktop-select-btn" onClick={handleVoiceSelect}>
                Select {currentVoiceData?.name}
              </button>
            </div>
          </div>

          <style jsx>{`
            /* ⚡ PERFORMANCE OPTIMIZATIONS */
            * {
              -webkit-font-smoothing: antialiased;
              -moz-osx-font-smoothing: grayscale;
            }

            /* Overlay */
            .voice-modal-overlay {
              position: fixed;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;
              background: rgba(0, 0, 0, 0.4);
              display: flex;
              align-items: flex-end;
              justify-content: center;
              z-index: 10000;
              animation: fadeIn 0.25s ease-out;
              backdrop-filter: blur(8px);
              -webkit-backdrop-filter: blur(8px);
              will-change: opacity;
            }

            .voice-modal-overlay.animating-out {
              animation: fadeOut 0.25s ease-out forwards;
            }

            @keyframes fadeIn {
              from { opacity: 0; }
              to { opacity: 1; }
            }

            @keyframes fadeOut {
              to { opacity: 0; }
            }

            /* Main Container */
            .voice-modal-container {
              position: fixed;
              bottom: 0;
              left: 50%;
              transform: translateX(-50%) translateZ(0);
              width: 100%;
              max-width: 420px;
              height: 580px;
              background: linear-gradient(170deg, #fefefe 0%, #fefefe 60%, #e0e7fc 100%);
              border-radius: 28px 28px 0 0;
              padding: 16px 16px 40px;
              display: flex;
              flex-direction: column;
              align-items: center;
              animation: slideUp 0.35s cubic-bezier(0.25, 0.46, 0.45, 0.94);
              box-shadow: 0 -8px 32px rgba(0, 0, 0, 0.2);
              will-change: transform, opacity;
            }

            .voice-modal-container.slide-down {
              animation: slideDown 0.35s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
              pointer-events: none;
            }

            @keyframes slideUp {
              from { transform: translateX(-50%) translateY(100%) translateZ(0); }
              to { transform: translateX(-50%) translateY(0) translateZ(0); }
            }

            @keyframes slideDown {
              to { transform: translateX(-50%) translateY(100%) translateZ(0); }
            }

            /* Hide desktop layout by default */
            .desktop-layout {
              display: none;
            }

            .mobile-tablet-layout {
              display: flex;
              flex-direction: column;
              width: 100%;
              height: 100%;
            }

            /* ===== TABLET (768px - 1024px) - ENHANCED MOBILE LAYOUT ===== */
            @media (min-width: 768px) and (max-width: 1023px) {
              .voice-modal-overlay {
                align-items: center;
                background: rgba(0, 0, 0, 0.5);
                backdrop-filter: blur(12px);
              }

              .voice-modal-container {
                position: relative;
                left: auto;
                bottom: auto;
                transform: none;
                max-width: 600px;
                width: 90%;
                height: auto;
                border-radius: 36px;
                padding: 48px 40px;
                animation: scaleIn 0.35s cubic-bezier(0.25, 0.46, 0.45, 0.94);
              }

              .voice-modal-container.slide-down {
                animation: scaleOut 0.35s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
              }

              @keyframes scaleIn {
                from { transform: scale(0.9) translateZ(0); opacity: 0; }
                to { transform: scale(1) translateZ(0); opacity: 1; }
              }

              @keyframes scaleOut {
                to { transform: scale(0.9) translateZ(0); opacity: 0; }
              }

              .voice-modal-header {
                margin-bottom: 36px;
              }

              .voice-modal-title2 {
                font-size: 32px;
              }

              .voice-modal-close-button {
                width: 42px;
                height: 42px;
              }

              .voice-ai-buddy {
                width: 160px;
                height: 160px;
                margin-bottom: 32px;
              }

              .buddy-circle {
                width: 160px;
                height: 160px;
              }

              .buddy-inner {
                width: 105px;
                height: 105px;
              }

              .buddy-core-alex {
                width: 52px;
                height: 52px;
              }

              .wave-animation {
                width: 180px;
                height: 180px;
              }

              .voice-info {
                margin-top: 32px;
                margin-bottom: 16px;
              }

              .voice-name {
                font-size: 32px;
                margin-bottom: 10px;
              }

              .voice-personality {
                font-size: 18px;
              }

              .voice-controls {
                margin-top: 32px;
                gap: 20px;
              }

              .nav-button {
                width: 46px;
                height: 46px;
                font-size: 24px;
              }

              .preview-button {
                padding: 14px 32px;
                font-size: 17px;
                font-weight: 500;
              }

              .voice-indicators {
                margin: 28px 0;
                gap: 10px;
              }

              .indicator {
                width: 7px;
                height: 7px;
              }

              .indicator.active {
                width: 28px;
              }

              .select-voice-button {
                max-width: 280px;
                padding: 16px 40px;
                font-size: 18px;
                font-weight: 500;
                margin-top: 32px;
              }

              .desktop-only {
                display: flex;
              }
            }

            /* ===== DESKTOP (1024px+) - GRID LAYOUT ===== */
            @media (min-width: 1024px) {
              .voice-modal-overlay {
                align-items: center;
                background: rgba(0, 0, 0, 0.5);
                backdrop-filter: blur(16px);
                -webkit-backdrop-filter: blur(16px);
              }

              .voice-modal-container {
                position: relative;
                left: auto;
                bottom: auto;
                transform: none;
                max-width: 750px;
                width: auto;
                height: auto;
                border-radius: 36px;
                padding: 44px 48px 48px;
                animation: scaleIn 0.35s cubic-bezier(0.25, 0.46, 0.45, 0.94);
              }

              .voice-modal-container.slide-down {
                animation: scaleOut 0.35s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
              }

              @keyframes scaleIn {
                from { transform: scale(0.9) translateZ(0); opacity: 0; }
                to { transform: scale(1) translateZ(0); opacity: 1; }
              }

              @keyframes scaleOut {
                to { transform: scale(0.9) translateZ(0); opacity: 0; }
              }

              .mobile-tablet-layout {
                display: none;
              }

              .desktop-layout {
                display: flex;
                flex-direction: column;
                width: 100%;
              }

              .desktop-header {
                display: flex;
                align-items: center;
                justify-content: center;
                position: relative;
                margin-bottom: 36px;
              }

              .desktop-title {
                font-size: 32px;
                font-weight: 500;
                color: #272727;
                margin: 0;
              }

              .voices-grid {
                display: grid;
                grid-template-columns: repeat(2, 1fr);
                gap: 20px;
                margin-bottom: 32px;
              }

              .voice-card {
                background: rgba(255, 255, 255, 0.6);
                border: 2px solid rgba(0, 0, 0, 0.08);
                border-radius: 24px;
                padding: 28px 24px;
                cursor: pointer;
                transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                position: relative;
                display: flex;
                flex-direction: column;
                align-items: center;
                text-align: center;
              }

              .voice-card:hover {
                transform: translateY(-4px);
                border-color: rgba(0, 0, 0, 0.12);
                box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
                background: rgba(255, 255, 255, 0.8);
              }

              .voice-card.selected {
                border-color: #1e76f2;
                background: rgba(30, 118, 242, 0.05);
                box-shadow: 0 4px 16px rgba(30, 118, 242, 0.2);
              }

              .voice-card-orb {
                margin-bottom: 20px;
              }

              .card-buddy-circle {
                width: 90px;
                height: 90px;
                border-radius: 50%;
                background: linear-gradient(145deg, #ffffff, #f0f0f0);
                box-shadow: 
                  6px 6px 12px rgba(16, 16, 16, 0.15),
                  -6px -6px 12px rgba(255, 255, 255, 0.8),
                  inset 2px 2px 4px rgba(0, 0, 0, 0.1);
                display: flex;
                align-items: center;
                justify-content: center;
                transition: transform 0.3s ease;
              }

              .voice-card:hover .card-buddy-circle {
                transform: scale(1.05);
              }

              .card-buddy-circle.speaking {
                animation: speaking-pulse 1.5s infinite ease-in-out;
              }

              @keyframes speaking-pulse {
                0%, 100% { 
                  transform: scale(1);
                  box-shadow: 
                    6px 6px 12px rgba(16, 16, 16, 0.15),
                    -6px -6px 12px rgba(255, 255, 255, 0.8),
                    inset 2px 2px 4px rgba(0, 0, 0, 0.1),
                    0 0 0 0 rgba(74, 144, 226, 0);
                }
                50% { 
                  transform: scale(1.08);
                  box-shadow: 
                    6px 6px 12px rgba(16, 16, 16, 0.15),
                    -6px -6px 12px rgba(255, 255, 255, 0.8),
                    inset 2px 2px 4px rgba(0, 0, 0, 0.1),
                    0 0 16px 3px rgba(74, 144, 226, 0.4);
                }
              }

              .card-buddy-inner {
                width: 60px;
                height: 60px;
                border-radius: 50%;
                background: linear-gradient(145deg, #f8f9fa, #e9ecef);
                box-shadow: inset 2px 2px 4px rgba(0, 0, 0, 0.12);
                display: flex;
                align-items: center;
                justify-content: center;
              }

              .card-buddy-core {
                width: 30px;
                height: 30px;
                border-radius: 50%;
                background: linear-gradient(145deg, #6c757d, #5a6268);
                box-shadow: 
                  2px 2px 4px rgba(0, 0, 0, 0.2),
                  -1px -1px 2px rgba(255, 255, 255, 0.3);
              }

              .voice-card-info {
                width: 100%;
              }

              .voice-card-name {
                font-size: 22px;
                font-weight: 500;
                color: #272727;
                margin: 0 0 6px 0;
              }

              .voice-card-personality {
                font-size: 14px;
                color: #666;
                opacity: 0.8;
                margin: 0;
              }

              .selected-checkmark {
                position: absolute;
                top: 16px;
                right: 16px;
                width: 32px;
                height: 32px;
                border-radius: 50%;
                background: #1e76f2;
                color: white;
                display: flex;
                align-items: center;
                justify-content: center;
                animation: checkmarkPop 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
              }

              @keyframes checkmarkPop {
                0% { transform: scale(0); }
                100% { transform: scale(1); }
              }

              .desktop-actions {
                display: flex;
                gap: 16px;
                width: 100%;
              }

              .desktop-preview-btn {
                flex: 1;
                padding: 16px 28px;
                background: rgba(228, 228, 228, 0.7);
                border: 1px solid rgba(214, 214, 214, 1);
                border-radius: 24px;
                color: #333;
                font-size: 16px;
                font-weight: 500;
                cursor: pointer;
                transition: all 0.2s ease;
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 10px;
              }

              .desktop-preview-btn:hover {
                background: rgba(228, 228, 228, 0.9);
                transform: translateY(-2px);
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
              }

              .desktop-preview-btn.playing {
                background: rgba(239, 68, 68, 0.1);
                border-color: rgba(239, 68, 68, 0.3);
                color: #dc2626;
              }

              .desktop-select-btn {
                flex: 1.5;
                padding: 16px 36px;
                background: linear-gradient(135deg, #1e76f2 0%, #86bfff 100%);
                border: 1px solid rgba(238, 238, 238, 0.3);
                border-radius: 24px;
                color: #ffffff;
                font-size: 17px;
                font-weight: 500;
                cursor: pointer;
                transition: all 0.2s ease;
                box-shadow: 0 4px 12px rgba(30, 118, 242, 0.3);
                display: flex;
                align-items: center;
                justify-content: center;
              }

              .desktop-select-btn:hover {
                transform: translateY(-2px);
                box-shadow: 0 6px 18px rgba(30, 118, 242, 0.4);
              }
            }

            /* Mobile/Tablet Common Styles */
            .voice-modal-header {
              display: flex;
              align-items: center;
              justify-content: center;
              width: 100%;
              position: relative;
              margin-bottom: 24px;
            }

            .voice-modal-title2 {
              color: #272727ff;
              font-size: 25px;
              font-weight: 500;
              margin: 0;
            }

            .voice-modal-close-button {
              position: absolute;
              right: 0;
              width: 34px;
              height: 34px;
              border-radius: 50%;
              border: 1px solid rgba(0, 0, 0, 0.1);
              background: rgba(255, 255, 255, 0.9);
              color: #666;
              cursor: pointer;
              display: flex;
              align-items: center;
              justify-content: center;
              transition: all 0.2s ease;
              box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
            }

            .voice-modal-close-button:hover {
              background: rgba(255, 255, 255, 1);
              color: #333;
              transform: scale(1.05);
            }

            .voice-modal-content {
              display: flex;
              flex-direction: column;
              align-items: center;
              width: 100%;
            }

            .voice-ai-buddy {
              position: relative;
              display: flex;
              align-items: center;
              justify-content: center;
              margin-bottom: 20px;
              width: 90px;
              height: 90px;
            }

            .buddy-circle {
              width: 90px;
              height: 90px;
              border-radius: 50%;
              background: linear-gradient(145deg, #ffffff, #f0f0f0);
              box-shadow: 
                8px 8px 16px rgba(16, 16, 16, 0.2),
                -8px -8px 16px rgba(255, 255, 255, 0.8),
                inset 2px 2px 4px rgba(0, 0, 0, 0.15);
              display: flex;
              align-items: center;
              justify-content: center;
              position: relative;
              transition: transform 0.3s ease;
              cursor: pointer;
              transform: translate3d(0, 0, 0);
              will-change: transform;
            }

            .buddy-circle:hover {
              transform: scale(1.05) translate3d(0, 0, 0);
            }

            .buddy-circle:active {
              transform: scale(0.98) translate3d(0, 0, 0);
            }

            .buddy-circle.speaking {
              animation: speaking-pulse 1.5s infinite ease-in-out;
            }

            .buddy-circle.swipe-left {
              animation: swipeLeftFeedback 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
            }

            .buddy-circle.swipe-right {
              animation: swipeRightFeedback 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
            }

            @keyframes swipeLeftFeedback {
              0% { transform: translateX(0) scale(1) translateZ(0); opacity: 1; }
              30% { transform: translateX(-20px) scale(0.95) translateZ(0); opacity: 0.8; }
              60% { transform: translateX(10px) scale(1.05) translateZ(0); opacity: 0.9; }
              100% { transform: translateX(0) scale(1) translateZ(0); opacity: 1; }
            }

            @keyframes swipeRightFeedback {
              0% { transform: translateX(0) scale(1) translateZ(0); opacity: 1; }
              30% { transform: translateX(20px) scale(0.95) translateZ(0); opacity: 0.8; }
              60% { transform: translateX(-10px) scale(1.05) translateZ(0); opacity: 0.9; }
              100% { transform: translateX(0) scale(1) translateZ(0); opacity: 1; }
            }

            .buddy-inner {
              width: 60px;
              height: 60px;
              border-radius: 50%;
              background: linear-gradient(145deg, #f8f9fa, #e9ecef);
              box-shadow: inset 2px 2px 4px rgba(0, 0, 0, 0.15);
              display: flex;
              align-items: center;
              justify-content: center;
              position: relative;
            }

            .buddy-core-alex {
              width: 30px;
              height: 30px;
              border-radius: 50%;
              background: linear-gradient(145deg, #6c757d, #5a6268);
              box-shadow: 
                2px 2px 4px rgba(0, 0, 0, 0.2),
                -2px -2px 4px rgba(255, 255, 255, 0.5);
              transition: transform 0.3s ease;
            }

            .wave-animation {
              position: absolute;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%);
              width: 110px;
              height: 110px;
              border-radius: 50%;
              border: 0.5px solid rgba(104, 110, 116, 0.2);
              animation: wave 3s infinite ease-in-out;
              opacity: 0;
              will-change: transform, opacity;
            }

            @keyframes wave {
              0% { transform: translate(-50%, -50%) scale(1); opacity: 0.5; }
              50% { transform: translate(-50%, -50%) scale(1.2); opacity: 0.1; }
              100% { transform: translate(-50%, -50%) scale(1); opacity: 0.5; }
            }

            .voice-info {
              text-align: center;
              color: #333;
              margin-top: 20px;
              margin-bottom: 8px;
            }

            .voice-name {
              font-size: 18px;
              font-weight: 500;
              margin: 0 0 4px 0;
              color: #333;
            }

            .voice-personality {
              font-size: 13px;
              margin: 4px;
              opacity: 0.6;
              color: #666;
              text-transform: none;
            }

            .voice-controls {
              display: flex;
              align-items: center;
              justify-content: center;
              position: relative;
              width: 100%;
              gap: 15px;
              margin-top: 20px;
            }

            .nav-button {
              position: absolute;
              width: 32px;
              height: 32px;
              border: 2px solid rgba(0, 0, 0, 0.1);
              background: rgba(255, 255, 255, 0.9);
              color: #666;
              font-size: 18px;
              cursor: pointer;
              transition: all 0.2s ease;
              display: flex;
              align-items: center;
              justify-content: center;
              box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
              top: 50%;
              transform: translateY(-50%);
            }

            .nav-button.prev {
              left: -8px;
              border-radius: 25px 8px 8px 25px;
            }

            .nav-button.next {
              right: -8px;
              border-radius: 8px 25px 25px 8px;
            }

            .nav-button:hover {
              background: rgba(255, 255, 255, 1);
              color: #333;
              transform: translateY(-50%) scale(1.05);
            }

            .preview-button {
              padding: 8px 18px;
              background: rgba(228, 228, 228, 0.6);
              border: 1px solid rgba(214, 214, 214, 1);
              border-radius: 20px;
              color: #333;
              font-size: 13px;
              font-weight: 400;
              cursor: pointer;
              transition: all 0.2s ease;
              box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
            }

            .preview-button:hover {
              transform: translateY(-1px);
              box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
            }

            .preview-button.playing {
              animation: pulse-button 1.5s infinite ease-in-out;
            }

            @keyframes pulse-button {
              0%, 100% { opacity: 0.7; transform: scale(1); }
              50% { opacity: 1; transform: scale(1.02); }
            }

            .voice-indicators {
              display: flex;
              gap: 6px;
              margin: 18px 0;
            }

            .indicator {
              width: 5px;
              height: 5px;
              border-radius: 50%;
              background: rgba(160, 160, 160, 0.7);
              cursor: pointer;
              transition: all 0.3s ease;
            }

            .indicator.active {
              background: #555;
              width: 20px;
              border-radius: 3px;
            }

            .select-voice-button {
              width: 100%;
              max-width: 170px;
              padding: 12px 24px;
              background: linear-gradient(135deg, #1e76f2 0%, #86bfff 100%);
              border: 1px solid rgba(238, 238, 238, 0.3);
              border-radius: 20px;
              color: #ffffff;
              font-size: 15px;
              font-weight: 500;
              cursor: pointer;
              transition: all 0.2s ease;
              box-shadow: 0 4px 12px rgba(30, 118, 242, 0.3);
              display: flex;
              align-items: center;
              justify-content: center;
              margin: 20px auto 0;
              white-space: nowrap;
            }

            .select-voice-button:hover {
              transform: translateY(-2px);
              box-shadow: 0 6px 16px rgba(30, 118, 242, 0.4);
            }

            .desktop-only { display: flex; }
            .mobile-only { display: none; }
            
            .swipe-instruction {
              color: black;
              font-size: 12px;
              opacity: 0.6;
              margin-top: 20px;
              text-align: center;
            }

            @media (max-width: 767px) {
              .voice-modal-container {
                max-width: 100%;
                height: 90vh;
                border-radius: 24px 24px 0 0;
                padding: 12px 12px 28px;
              }

              .voice-modal-title2 {
                font-size: 20px;
              }

              .voice-name {
                font-size: 15px;
              }

              .voice-personality {
                font-size: 11px;
              }

              .preview-button {
                padding: 6px 14px;
                font-size: 11px;
              }

              .select-voice-button {
                max-width: 140px;
                padding: 10px 18px;
                font-size: 13px;
              }

              .voice-ai-buddy {
                margin-bottom: 14px;
                width: 70px;
                height: 70px;
              }

              .buddy-circle {
                width: 70px;
                height: 70px;
              }

              .buddy-inner {
                width: 48px;
                height: 48px;
              }

              .buddy-core-alex {
                width: 24px;
                height: 24px;
              }

              .voice-info {
                margin-top: 12px;
                margin-bottom: 6px;
              }

              .voice-controls {
                gap: 12px;
                margin-top: 14px;
              }

              .voice-indicators {
                margin: 14px 0;
              }

              .swipe-instruction {
                font-size: 10px;
                margin-top: 14px;
              }

              .voice-modal-header {
                margin-bottom: 16px;
              }

              .desktop-only { display: none; }
              .mobile-only { display: block; }
            }
          `}</style>
        </div>
      </div>
    );
  }

  return null;
};

export default VoiceModeModal;